<template>
  <content-dialog
    v-model="dialogOpen"
    fullscreen-mobile
    title="Recurring Rule"
    persistent
    tags="recurring rule"
    :width="withEstimates ? '800px' : '350px'"
    @hide="$emit('close')"
  >
    <q-list
      v-if="!!$slots.default"
      padding
    >
      <slot />
      <q-separator inset />
    </q-list>

    <div class="row">
      <div
        class="col-12"
        :class="{'col-sm-7': withEstimates}"
      >
        <q-list>
          <q-expansion-item
            v-if="defaultRruleSelectionOptions.length > 0"
            ref="defaultsExpander"
            label="Default Options"
            header-class="text-bold initial-line-height"
            expand-icon="mdi-chevron-right"
            expanded-icon="mdi-chevron-down"
            class="q-mb-sm"
            group="ruleExpander"
          >
            <q-list dense>
              <q-item-wrapper
                tag="label"
                dense
              >
                <q-checkbox
                  v-model="onlyWeekdays"
                  label="Only Weekdays"
                  size="sm"
                />
              </q-item-wrapper>
              <q-separator inset spaced />

              <q-item-wrapper
                v-for="(defRule, index) in defaultRruleSelectionOptions"
                :key="index"
                :label="defRule.label"
                :active="hasMadeChange && isDefaultRuleSelected(defRule)"
                :icon="defRule.icon || 'none'"
                clickable
                @click="selectDefaultRule(defRule)"
              >
                <template v-if="!!defRule.params.count" #after>
                  <q-item-section side>
                    <q-item-label caption>
                      {{ defRule.params.count }} {{ pluraliseString('time', defRule.params.count) }}
                    </q-item-label>
                  </q-item-section>
                </template>
              </q-item-wrapper>
            </q-list>
          </q-expansion-item>

          <q-expansion-item
            ref="advancedExpander"
            label="All Options"
            header-class="text-bold initial-line-height"
            expand-icon="mdi-chevron-right"
            expanded-icon="mdi-chevron-down"
            group="ruleExpander"
          >
            <q-list>
              <q-item-wrapper>
                <q-input
                  stack-label
                  outlined
                  label="Interval"
                  prefix="Every"
                  type="number"
                  :model-value="selected.interval"
                  :min="1"
                  :max="maxIntervalForFrequency"
                  class="borderless-children"
                  @update:model-value="onInputInterval"
                >
                  <template #append>
                    <q-select
                      v-model="selected.frequency"
                      dense
                      borderless
                      :options="frequencyOptions"
                      map-options
                      emit-value
                      hide-dropdown-icon
                      hide-bottom-space
                      behavior="menu"
                      label="Frequency"
                      @update:model-value="onInputFrequency"
                    >
                      <template #selected-item>
                        <span>{{ intervalSuffix }}</span>
                        <span>
                          <q-icon
                            name="mdi-menu-down"
                            size="1.5em"
                            class="q-mb-xs q-ml-sm"
                          />
                        </span>
                      </template>
                    </q-select>
                  </template>
                </q-input>
              </q-item-wrapper>

              <q-item-wrapper>
                <q-input
                  stack-label
                  outlined
                  label="Count"
                  suffix="times"
                  type="number"
                  :model-value="selected.count"
                  :min="1"
                  :max="maxCountForFrequency"
                  @update:model-value="onInputCount"
                />
              </q-item-wrapper>

              <q-item-wrapper>
                <multi-select
                  :model-value="selected.byweekday"
                  outlined
                  stack-label
                  label="Only on specific days"
                  hint="Deselect all options to allow any day"
                  :options="byWeekdayOptions"
                  clearable
                  emit-value
                  map-options
                  @update:model-value="onInputDay"
                />
              </q-item-wrapper>

              <q-item-wrapper v-if="selected.byweekday && selected.byweekday.length > 0 && selected.frequency !== RRule.DAILY">
                <q-select
                  v-model="selected.bysetpos"
                  :options="bySetPosOptions"
                  :disable="cannotChangeSetpos"
                  outlined
                  dense
                  emit-value
                  map-options
                />
              </q-item-wrapper>

              <q-item-wrapper>
                <multi-select
                  :model-value="selected.bymonth"
                  outlined
                  stack-label
                  label="Only on specific months"
                  hint="Deselect all options to allow any month"
                  :options="byMonthOptions"
                  clearable
                  emit-value
                  map-options
                  @update:model-value="onInputMonth"
                />
              </q-item-wrapper>
            </q-list>
          </q-expansion-item>
        </q-list>

        <q-separator
          v-if="withEstimates"
          class="lt-sm"
          inset
          spaced
        />
      </div>

      <div
        v-if="withEstimates"
        class="col-12 col-sm-5 q-px-sm"
      >
        <q-card class="bg-grey-2" flat>
          <q-list padding>
            <q-item-wrapper
              :label="`This recurring rule will occur ${rrule.toText()}`"
              label-class="text-bold"
              dense
            />
            <ul class="q-mb-none q-mt-sm">
              <transition-group name="growRightTransition">
                <li
                  v-if="!ruleBeginsOnStartDate"
                  key="initial"
                  class="text-caption text-bold"
                >
                  <q-item-label class="text-bold text-orange-10">
                    The start date will differ from the rule
                  </q-item-label>

                  {{ $formatDate(startingFrom, luxonFormats.SHORT_WORDED_DATE) }}
                  <span v-if="withEstimatedTime"> at {{ $formatDate(startingFrom, luxonFormats.TIME) }}</span>

                  <q-separator spaced />
                </li>

                <li
                  v-for="(date, key) in allDates"
                  :key="date"
                  :class="{'text-bold': key === 0 || key === allDates.length - 1}"
                  class="text-caption"
                >
                  {{ $formatDate(date, luxonFormats.SHORT_WORDED_DATE) }}
                  <span v-if="withEstimatedTime"> at {{ $formatDate(startingFrom, luxonFormats.TIME) }}</span>
                </li>
              </transition-group>
            </ul>
          </q-list>
        </q-card>
      </div>
    </div>

    <template #bottom>
      <q-toolbar>
        <q-btn
          v-close-popup
          label="Remove"
          flat
          color="red"
          @click="$emit('remove')"
        />
        <q-space />
        <q-btn
          v-close-popup
          label="Save Rule"
          color="green"
          icon-right="mdi-check"
          @click="generateRrule"
        />
      </q-toolbar>
    </template>
  </content-dialog>
</template>

<script>
import { markRaw } from 'vue'

import { RRule } from 'rrule'
import rruleMixin from './rruleMixin'

const expansionDefaultsName = 'defaults'
const expansionAdvancedName = 'advanced'

function defaultRruleOptions (params = {}) {
  return {
    frequency: params.frequency ?? RRule.YEARLY,
    count: params.count ?? 5,
    interval: params.interval ?? 1,
    byweekday: params.byweekday ?? null,
    bymonth: params.bymonth ?? null,
    bysetpos: params.bysetpos !== undefined ? params.bysetpos : 1
  }
}

/**
 * Dialog for creating a recurring rule
 *
 * SLOTS:
 * - default: Displayed at the top of the dialog with additional details
 *
 * EVENTS:
 * - close: Emit when dialog is closed
 * - remove: Emit when the "remove" button is clicked - denoting delete the rule
 * - save: Emit when "Save" is clicked
 */
export default {
  mixins: [rruleMixin],
  props: {
    // Date that the rule starts from (optional)
    startingFrom: {
      type: String,
      default: null
    },
    // Display a list of estimated dates for the rule
    withEstimates: {
      type: Boolean,
      default: false
    },
    // Display the time as well from the startingFrom^ string
    withEstimatedTime: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'remove', 'save'],
  data () {
    return {
      RRule: markRaw(RRule),
      hasMadeChange: false,
      dialogOpen: false,
      onlyWeekdays: false,
      frequencyOptions: [
        { label: 'Years', value: RRule.YEARLY },
        { label: 'Months', value: RRule.MONTHLY },
        { label: 'Weeks', value: RRule.WEEKLY },
        { label: 'Days', value: RRule.DAILY }
      ],
      byWeekdayOptions: [
        { label: 'Monday', value: RRule.MO.weekday },
        { label: 'Tuesday', value: RRule.TU.weekday },
        { label: 'Wednesday', value: RRule.WE.weekday },
        { label: 'Thursday', value: RRule.TH.weekday },
        { label: 'Friday', value: RRule.FR.weekday },
        { label: 'Saturday', value: RRule.SA.weekday },
        { label: 'Sunday', value: RRule.SU.weekday }
      ],
      byMonthOptions: [
        { label: 'January', value: 1 },
        { label: 'February', value: 2 },
        { label: 'March', value: 3 },
        { label: 'April', value: 4 },
        { label: 'May', value: 5 },
        { label: 'June', value: 6 },
        { label: 'July', value: 7 },
        { label: 'August', value: 8 },
        { label: 'September', value: 9 },
        { label: 'October', value: 10 },
        { label: 'November', value: 11 },
        { label: 'December', value: 12 }
      ],
      selected: defaultRruleOptions()
    }
  },
  computed: {
    intervalName () {
      switch (this.selected.frequency) {
        case RRule.YEARLY:
          return 'year'
        case RRule.MONTHLY:
          return 'month'
        case RRule.WEEKLY:
          return 'week'
        default:
        case RRule.DAILY:
          return 'day'
      }
    },
    intervalSuffix () {
      if (this.selected.interval < 2) {
        switch (this.selected.frequency) {
          case RRule.YEARLY:
            return 'Year'
          case RRule.MONTHLY:
            return 'Month'
          case RRule.WEEKLY:
            return 'Week'
          default:
          case RRule.DAILY:
            return 'Day'
        }
      }
      else {
        switch (this.selected.frequency) {
          case RRule.YEARLY:
            return 'Years'
          case RRule.MONTHLY:
            return 'Months'
          case RRule.WEEKLY:
            return 'Weeks'
          default:
          case RRule.DAILY:
            return 'Days'
        }
      }
    },
    maxCountForFrequency () {
      switch (this.selected.frequency) {
        case RRule.YEARLY:
          return 20
        case RRule.MONTHLY:
          return 120
        case RRule.WEEKLY:
          return 104
        default:
        case RRule.DAILY:
          return 365
      }
    },
    maxIntervalForFrequency () {
      switch (this.selected.frequency) {
        case RRule.YEARLY:
          return 20
        case RRule.MONTHLY:
          return 120
        case RRule.WEEKLY:
          return 520
        default:
        case RRule.DAILY:
          return 365
      }
    },
    bySetPosOptions () {
      return [
        { label: 'Only use first occurrence of specific day within each ' + this.intervalName, value: 1 },
        { label: 'Use all occurrences of specific days within each ' + this.intervalName, value: null },
        { label: 'Only use last occurrence of specific day within each ' + this.intervalName, value: -1 }
      ]
    },
    // Create default options that the user can click on to quickly fill in a recurring rule
    defaultRruleSelectionOptions () {
      const arr = [
        {
          label: 'Weekly',
          icon: 'mdi-calendar-week',
          params: defaultRruleOptions({
            frequency: RRule.WEEKLY,
            count: 12,
            interval: 1,
            bysetpos: null
          })
        },
        {
          label: 'Monthly',
          icon: 'mdi-calendar-month',
          params: defaultRruleOptions({
            frequency: RRule.MONTHLY,
            count: 12,
            interval: 1,
            bysetpos: null
          })
        },
        {
          label: 'Every 3 months',
          params: defaultRruleOptions({
            frequency: RRule.MONTHLY,
            count: 10,
            interval: 3,
            bysetpos: null
          })
        },
        {
          label: 'Every 4 months',
          params: defaultRruleOptions({
            frequency: RRule.MONTHLY,
            count: 10,
            interval: 4,
            bysetpos: null
          })
        },
        {
          label: 'Every 6 months',
          params: defaultRruleOptions({
            frequency: RRule.MONTHLY,
            count: 10,
            interval: 6,
            bysetpos: null
          })
        },
        {
          label: 'Yearly',
          icon: 'mdi-calendar',
          params: defaultRruleOptions({
            frequency: RRule.YEARLY,
            count: 5,
            interval: 1,
            bysetpos: null
          })
        }
      ]

      if (this.onlyWeekdays) {
        arr.forEach(defRule => {
          if (defRule.params.byweekday) {
            return
          }
          defRule.params.byweekday = this.byWeekdayOptions.filter((_, index) => index < 5).map(item => item.value)
          defRule.params.bysetpos = 1
        })
      }

      return arr
    },
    rrule () {
      const options = {
        freq: this.selected.frequency,
        interval: Math.min(parseInt(this.selected.interval), this.maxIntervalForFrequency),
        count: Math.min(parseInt(this.selected.count), this.maxCountForFrequency),
        byweekday: this.selected.byweekday,
        bymonth: this.selected.bymonth,
        bysetpos: this.selected.bysetpos,
        bymonthday: null,
        byyearday: null
      }

      if (this.startingFrom) {
        options.dtstart = this.luxon.fromSQL(this.startingFrom, { zone: 'UTC' }).toJSDate()

        const { bysetpos, bymonthday, byyearday, byweekday, bymonth } = this.getSpecificRecurringFixes(
          this.selected.frequency,
          this.selected.byweekday
        )
        options.bymonthday = bymonthday
        options.byyearday = byyearday
        options.byweekday = byweekday
        if (bysetpos !== null) {
          options.bysetpos = bysetpos
        }
        if (bymonth !== null && (!options.bymonth || options.bymonth.length < 1)) {
          options.bymonth = bymonth
        }
      }

      // Fixes a problem where the backend will fail to generate the date
      // because bysetpos can only be used in combination with one of the other BY options
      if (
        (!options.byweekday || options.byweekday.length < 1) &&
        (!options.bymonth || options.bymonth.length < 1) &&
        (!options.bymonthday || options.bymonthday.length < 1) &&
        (!options.byyearday || options.byyearday.length < 1)
      ) {
        options.bysetpos = null
      }

      return new RRule(options)
    },
    estimatedEndDate () {
      const allRules = this.rrule.all()
      return this.luxon.fromSQL(allRules[allRules.length - 1]).toSimpleSQL()
    },
    cannotChangeSetpos () {
      const currDate = this.luxon.fromSQL(this.startingFrom)
      return this.selected.frequency === RRule.MONTHLY &&
        (currDate.day === 31 || currDate.day === 30 || currDate.day === 29)
    },
    allDates () {
      // Set zone to UTC for displaying because it attempts to set timezone to AEST with conversion
      return this.rrule.all().map(item => this.luxon.fromJSDate(item, { zone: 'UTC' }).toSimpleSQL())
    },
    ruleBeginsOnStartDate () {
      return this.allDates.length > 0 && this.luxon.fromSQL(this.allDates[0]).hasSame(this.luxon.fromSQL(this.startingFrom), 'day')
    }
  },
  watch: {
    'selected': {
      handler () {
        this.hasMadeChange = true
      },
      deep: true
    }
  },
  methods: {
    setOpenedExpansionGroup (val) {
      switch (val) {
        case expansionDefaultsName:
          if (this.$refs.defaultsExpander) {
            this.$refs.defaultsExpander.show()
          }
          break
        case expansionAdvancedName:
          this.$refs.advancedExpander.show()
          break
      }
    },
    // Set non-UI values when creating/updating the rule
    getSpecificRecurringFixes (freq, weekdays, currDate) {
      return this.baseGetSpecificRecurringFixes(freq, weekdays, (currDate || this.startingFrom))
    },
    open (fromStr = null) {
      this.dialogOpen = true

      let useDefault = true

      // If a Rrule is being editing, pass in the string which was previously generated
      if (fromStr) {
        try {
          const newRrule = RRule.fromString(fromStr)
          const { byweekday, bymonth, bysetpos } = newRrule.origOptions
          this.selected.frequency = newRrule.origOptions.freq
          this.selected.count = parseInt(newRrule.origOptions.count ?? 1)
          this.selected.interval = parseInt(newRrule.origOptions.interval ?? 1)
          this.selected.byweekday = byweekday ? byweekday.map(item => item.weekday) : null
          this.selected.bymonth = Array.isArray(bymonth) ? bymonth : (bymonth ? [bymonth] : null)
          this.selected.bysetpos = Array.isArray(bysetpos) ? bysetpos[0] : (bysetpos || null)

          useDefault = false
          this.timed(() => {
            this.hasMadeChange = true
            this.onlyWeekdays = Array.isArray(this.selected.byweekday)
            this.setOpenedExpansionGroup(this.hasSelectedAnyDefaultRule() ? expansionDefaultsName : expansionAdvancedName)
          })
        }
        catch (err) {}
      }

      if (useDefault) {
        this.selected = defaultRruleOptions()

        this.timed(() => {
          this.hasMadeChange = false
          this.onlyWeekdays = false
          this.setOpenedExpansionGroup(this.defaultRruleSelectionOptions.length < 1 ? expansionAdvancedName : expansionDefaultsName)
        })
      }
    },
    selectDefaultRule (defRule) {
      this.selected = JSON.parse(JSON.stringify(defRule.params))
    },
    // Check if any of the default rules have been selected
    hasSelectedAnyDefaultRule () {
      const selectedRule = JSON.stringify(this.selected)
      return !!this.defaultRruleSelectionOptions.find(defRule => selectedRule === JSON.stringify(defRule.params))
    },
    isDefaultRuleSelected (defRule) {
      return JSON.stringify(this.selected) === JSON.stringify(defRule.params)
    },
    generateRrule () {
      const rruleString = this.getNewRrule()
      this.$emit('save', rruleString)
    },
    getNewRrule () {
      return this.rrule.toString()
    },
    onInputDay (val) {
      if (!this.selected.byweekday && val && val.length > 0) {
        this.selected.bysetpos = 1
      }

      if (!val || val.length < 1) {
        this.selected.byweekday = null
      }
      else {
        this.selected.byweekday = val
      }
    },
    onInputMonth (val) {
      if (!val || val.length < 1) {
        this.selected.bymonth = null
      }
      else {
        this.selected.bymonth = val
      }
    },
    onInputInterval (val) {
      val = parseInt(val)
      if (isNaN(val) || !val || val < 1) {
        this.selected.interval = 1
      }
      else {
        this.selected.interval = val
      }
    },
    onInputCount (val) {
      val = parseInt(val)
      if (isNaN(val) || !val || val < 1) {
        this.selected.count = 1
      }
      else {
        this.selected.count = val
      }
    },
    onInputFrequency (val) {
      // The interval and count are usually typed before the frequency is picked, so the
      // per-frequency limits are only applied here and when the rule is built
      this.selected.interval = Math.min(this.selected.interval, this.maxIntervalForFrequency)
      this.selected.count = Math.min(this.selected.count, this.maxCountForFrequency)

      const { bysetpos, bymonth } = this.getSpecificRecurringFixes(val)
      if (bysetpos !== null) {
        this.selected.bysetpos = bysetpos
      }
      if (bymonth !== null && (!this.selected.bymonth || this.selected.bymonth.length < 1)) {
        this.selected.bymonth = bymonth
      }
    }
  }
}
</script>
